import * as React from 'react';
import { cn } from '@/lib/utils';

type FloatingVariant = 'input' | 'textarea';

interface FloatingInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
    'size'
  > {
  label?: string;
  containerClassName?: string;
  variant?: FloatingVariant;
  containerStyle?: React.CSSProperties;
  endAdornment?: React.ReactNode;
}

/**
 * FloatingInput - A re-implementation of the MUI TextField component with a floating label.
 *
 * Behavior:
 * - When focused: Label floats up and sits on the border
 * - When blurred: Label returns to its original position (top-left for textarea, centered for input)
 *
 * Implementation details:
 * - Uses forwardRef to allow parent components to access the underlying DOM element
 *   (useful for programmatic focus, form libraries, or imperative operations)
 * - Label uses the theme background to mask the border while floating
 * - Supports both single-line inputs and multi-line textareas via the `variant` prop
 *
 * @param variant - 'input' for single-line, 'textarea' for multi-line (default: 'input')
 * @param label - Text displayed as the floating label
 * @param containerClassName - Custom classes for the outer container (useful for borders, sizing)
 */
export const FloatingInput = (props: FloatingInputProps) => {
  const {
    className,
    label,
    placeholder,
    containerClassName,
    variant = 'input',
    containerStyle,
    value,
    defaultValue,
    onFocus,
    onBlur,
    onChange,
    autoFocus,
    endAdornment,
    ...rest
  } = props;

  // Determine which HTML element to render (input or textarea) based on variant
  const InputElement = (variant === 'textarea' ? 'textarea' : 'input') as
    | 'input'
    | 'textarea';
  const isTextareaVariant = variant === 'textarea';

  const [isFocused, setIsFocused] = React.useState(false);
  const [hasContent, setHasContent] = React.useState(
    Boolean((value ?? defaultValue ?? '').toString())
  );
  const inputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // keep hasContent in sync for controlled inputs
  React.useEffect(() => {
    setHasContent(Boolean((value ?? defaultValue ?? '').toString()));
  }, [value, defaultValue]);

  // Auto-focus on mount if requested
  React.useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus, inputRef]);

  /**
   * Custom event handlers to handle focus, blur, and change events
   */
  const handleFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setIsFocused(true);
    setHasContent(Boolean(inputRef.current?.value));
    onFocus?.(e);
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setIsFocused(false);
    setHasContent(Boolean(inputRef.current?.value));
    onBlur?.(e);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setHasContent(Boolean(e.target.value));
    onChange?.(e);
  };

  // For textarea variant, click anywhere in the container to focus
  const handleContainerClick = () => {
    if (isTextareaVariant && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const labelText = label ?? placeholder ?? '';
  const isFloating = isFocused || hasContent;

  // Render container + control
  const hasAdornment = Boolean(endAdornment) && !isTextareaVariant;

  return (
    <div
      className={cn('relative w-full', containerClassName)}
      style={containerStyle}
      onClick={handleContainerClick}
    >
      <InputElement
        ref={inputRef as React.Ref<any>}
        value={value}
        defaultValue={defaultValue}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        placeholder={isFloating ? placeholder : ' '}
        aria-label={labelText || undefined}
        className={cn(
          'peer flex w-full rounded-md border border-input bg-background text-sm text-foreground shadow-sm transition-colors',
          'placeholder:text-muted-foreground',
          'hover:border-zinc-500 dark:hover:border-zinc-400',
          'focus-visible:outline-none focus-visible:ring-0 focus-visible:border-[#3B82F6]',
          'disabled:cursor-not-allowed disabled:opacity-50',
          isTextareaVariant
            ? 'min-h-[120px] resize-none py-3 px-4'
            : 'h-14 px-4 py-3',
          hasAdornment && !isTextareaVariant ? 'pr-16' : null,
          className
        )}
        {...rest}
      />
      {hasAdornment && (
        <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
          <div className="pointer-events-auto flex items-center">
            {endAdornment}
          </div>
        </div>
      )}
      {/* 
      The label is a sibling of the input and is positioned absolutely

      It will either sit in the top left border of the input or in the middle when not focused
      */}
      {labelText && (
        <label
          className={cn(
            'pointer-events-none absolute px-1 rounded-[6px] transition-all z-20 bg-background',
            isFloating
              ? [
                  '-top-[0.6rem] left-3 text-xs leading-4',
                  '[background:linear-gradient(to_bottom,transparent_0%,transparent_58%,white_58%,white_100%)]',
                  isFocused ? 'text-[#3B82F6]' : 'text-muted-foreground',
                ]
              : isTextareaVariant
                ? [
                    'top-3 left-4 translate-y-0 text-sm leading-5 text-muted-foreground',
                  ]
                : [
                    'top-1/2 left-3 -translate-y-1/2 text-sm leading-5 text-muted-foreground',
                  ]
          )}
        >
          {labelText}
        </label>
      )}
    </div>
  );
};
